import React, { useState, useEffect } from 'react';
import { Select, Input, InputNumber, Radio, Checkbox, DatePicker,
         Button, Card, Tag, Progress, Spin, message } from 'antd';
import { SendOutlined, SaveOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import axios from 'axios';
import { evaluateExpression } from '../../utils/formulaEval';
import coaImg  from '../../pages/assets/footer/coa.png';
import flagImg from '../../pages/assets/footer/flag.png';

const { TextArea } = Input;
const { Option }   = Select;

const API = window.location.hostname.includes('qoctesting')
    ? '/qoctesting/api/'
    : '/qoc/api/';

export default function QoCPublicSurveyForm({ match }) {
    const token = match?.params?.token;

    const [survey,       setSurvey]      = useState(null);
    const [facilities,   setFacilities]  = useState([]);
    const [facilityId,   setFacilityId]  = useState(null);
    const [respondent,   setRespondent]  = useState('');
    const [answers,      setAnswers]     = useState({});
    const [section,      setSection]     = useState(0);
    const [loading,      setLoading]     = useState(true);
    const [submitting,   setSubmitting]  = useState(false);
    const [submitted,    setSubmitted]   = useState(false);
    const [error,        setError]       = useState(null);
    // repeatCounts: { [groupLabel]: activeInstanceCount }
    const [repeatCounts, setRepeatCounts] = useState({});

    useEffect(() => {
        axios.get(API + 'survey/public/' + token)
            .then(r => {
                if (r.data.code !== 200) { setError(r.data.message); return; }
                setSurvey(r.data.data.survey);
                setFacilities(r.data.data.facilities);
                // Restore draft
                const draft = localStorage.getItem('pub_survey_' + token);
                if (draft) {
                    const d = JSON.parse(draft);
                    setAnswers(d.answers || {});
                    setFacilityId(d.facility_id || null);
                    setRespondent(d.respondent || '');
                    setRepeatCounts(d.repeatCounts || {});
                }
                // If this survey is scoped to one specific facility, the backend
                // returns exactly that one facility — auto-select it and skip the
                // picker entirely, since there's nothing to choose between.
                const facs = r.data.data.facilities || [];
                if (facs.length === 1) {
                    setFacilityId(facs[0].id);
                }
            })
            .catch(() => setError('Unable to load survey. Please check the link.'))
            .finally(() => setLoading(false));
    }, [token]); // eslint-disable-line

    const setAnswer = (qid, val) => {
        const a = { ...answers, [qid]: val };
        setAnswers(a);
        localStorage.setItem('pub_survey_' + token,
            JSON.stringify({ answers: a, facility_id: facilityId, respondent, repeatCounts }));
    };

    const setRepeatCount = (groupLabel, count, maxCount) => {
        const newCounts = { ...repeatCounts, [groupLabel]: Math.max(1, Math.min(count, maxCount)) };
        setRepeatCounts(newCounts);
        localStorage.setItem('pub_survey_' + token,
            JSON.stringify({ answers, facility_id: facilityId, respondent, repeatCounts: newCounts }));
    };

    const saveDraft = async () => {
        if (!facilityId) { message.warning('Please select your facility first'); return; }
        setSubmitting(true);
        try {
            const params = new URLSearchParams();
            params.append('facility_id', facilityId);
            params.append('respondent_name', respondent);
            Object.entries(answers).forEach(([k, v]) =>
                params.append(`answers[${k}]`,
                    (Array.isArray(v) || (v !== null && typeof v === 'object'))
                        ? JSON.stringify(v) : v));
            const r = await axios.post(API + 'survey/public/' + token + '/submit', params);
            if (r.data.code !== 200) throw new Error(r.data.message);
            message.success('Draft saved — you can return and continue later');
        } catch(e) { message.error(e.message || 'Save failed'); }
        finally { setSubmitting(false); }
    };

    const submit = async () => {
        if (!facilityId) { message.warning('Please select your facility'); return; }
        const required = currentQuestions.filter(q =>
            q.required && shouldShow(q) && !answers[q.id]);
        if (required.length > 0) {
            message.warning(`Please answer ${required.length} required question(s)`);
            return;
        }
        setSubmitting(true);
        try {
            const params = new URLSearchParams();
            params.append('facility_id', facilityId);
            params.append('respondent_name', respondent);
            params.append('submit', '1');
            Object.entries(answers).forEach(([k, v]) =>
                params.append(`answers[${k}]`, Array.isArray(v) ? JSON.stringify(v) : v));
            const r = await axios.post(API + 'survey/public/' + token + '/submit', params);
            if (r.data.code !== 200) throw new Error(r.data.message);
            localStorage.removeItem('pub_survey_' + token);
            setSubmitted(true);
        } catch(e) { message.error(e.message || 'Submission failed'); }
        finally { setSubmitting(false); }
    };

    // ── Skip logic ────────────────────────────────────────────────────────────
    const shouldShow = (q) => {
        let sl = q.skip_logic;
        if (!sl) return true;
        if (typeof sl === 'string') { try { sl = JSON.parse(sl); } catch(e) { return true; } }
        if (!sl?.field) return true;
        const { field, operator, value } = sl;
        const allQ = survey?.questions || [];
        const refQ = allQ.find(qq =>
            (qq.xlsform_name && qq.xlsform_name === field) ||
            String(qq.id) === String(field)
        );
        if (!refQ) return true;
        const refAnswer  = String(answers[refQ.id] || '').toLowerCase();
        const compareVal = String(value || '').toLowerCase();
        if (operator === '=')        return refAnswer === compareVal;
        if (operator === '!=')       return refAnswer !== compareVal;
        if (operator === 'contains') return refAnswer.includes(compareVal);
        return true;
    };

    // ── Question renderer ─────────────────────────────────────────────────────
    const renderQ = (q) => {
        const val = answers[q.id];
        switch (q.type) {
            case 'display':
                return null;
            case 'text':
                return <Input value={val||''} onChange={e=>setAnswer(q.id,e.target.value)} placeholder="Type your answer..."/>;
            case 'textarea':
                return <TextArea rows={3} value={val||''} onChange={e=>setAnswer(q.id,e.target.value)} placeholder="Type your answer..."/>;
            case 'number':
                return <InputNumber value={val} onChange={v=>setAnswer(q.id,v)} style={{width:'100%'}}/>;
            case 'single':
                return (
                    <Radio.Group value={val} onChange={e=>setAnswer(q.id,e.target.value)}
                        style={{display:'flex',flexDirection:'column',gap:8}}>
                        {(q.options||[]).map(opt=>(
                            <Radio key={opt} value={opt}
                                style={{padding:'10px 14px',border:'1px solid '+(val===opt?'#1890ff':'#e5e7eb'),
                                    borderRadius:8,background:val===opt?'#eff6ff':'#fff'}}>
                                {opt}
                            </Radio>
                        ))}
                    </Radio.Group>
                );
            case 'multiple':
                return (
                    <Checkbox.Group value={val||[]} onChange={v=>setAnswer(q.id,v)}
                        style={{display:'flex',flexDirection:'column',gap:8}}>
                        {(q.options||[]).map(opt=>(
                            <Checkbox key={opt} value={opt}
                                style={{padding:'10px 14px',border:'1px solid '+((val||[]).includes(opt)?'#1890ff':'#e5e7eb'),
                                    borderRadius:8,background:(val||[]).includes(opt)?'#eff6ff':'#fff'}}>
                                {opt}
                            </Checkbox>
                        ))}
                    </Checkbox.Group>
                );
            case 'scale':
                return (
                    <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {Array.from({length:(q.scale_max-q.scale_min)+1},(_,i)=>i+q.scale_min).map(n=>(
                            <button key={n} onClick={()=>setAnswer(q.id,n)}
                                style={{width:52,height:52,borderRadius:10,
                                    border:'2px solid '+(val===n?'#1890ff':'#e5e7eb'),
                                    background:val===n?'#1890ff':'#fff',
                                    color:val===n?'#fff':'#555',
                                    fontWeight:800,fontSize:18,cursor:'pointer',transition:'all 0.15s'}}>
                                {n}
                            </button>
                        ))}
                    </div>
                );
            case 'boolean':
                return (
                    <div style={{display:'flex',gap:12}}>
                        {['Yes','No'].map(opt=>(
                            <button key={opt} onClick={()=>setAnswer(q.id,opt)}
                                style={{flex:1,padding:'14px 0',borderRadius:10,
                                    border:'2px solid '+(val===opt?'#1890ff':'#e5e7eb'),
                                    background:val===opt?'#1890ff':'#fff',
                                    color:val===opt?'#fff':'#555',
                                    fontWeight:700,fontSize:16,cursor:'pointer'}}>
                                {opt==='Yes'?'✅ Yes':'❌ No'}
                            </button>
                        ))}
                    </div>
                );
            case 'dropdown':
                return (
                    <Select value={val} onChange={v=>setAnswer(q.id,v)}
                        placeholder="Select an option..." style={{width:'100%'}} size="large" allowClear>
                        {(q.options||[]).map(opt=><Option key={opt} value={opt}>{opt}</Option>)}
                    </Select>
                );
            case 'matrix': {
                const matCols = Array.isArray(q.columns) ? q.columns
                    : (typeof q.columns==='string' ? (() => { try { return JSON.parse(q.columns); } catch(e) { return ['Yes','No']; } })() : ['Yes','No']);
                const matRows = Array.isArray(q.rows) ? q.rows
                    : (typeof q.rows==='string' ? (() => { try { return JSON.parse(q.rows); } catch(e) { return []; } })() : []);
                return (
                    <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                            <thead>
                                <tr>
                                    <th style={{padding:'8px 12px',textAlign:'left',background:'#f8fafc',border:'1px solid #e2e8f0',width:'40%'}}></th>
                                    {matCols.map(col=>(
                                        <th key={col} style={{padding:'8px 12px',textAlign:'center',background:'#f8fafc',border:'1px solid #e2e8f0',fontWeight:600,color:'#374151'}}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {matRows.map((row,ri)=>(
                                    <tr key={ri} style={{background:ri%2===0?'#fff':'#f8fafc'}}>
                                        <td style={{padding:'8px 12px',border:'1px solid #e2e8f0',fontWeight:500,color:'#374151'}}>{row}</td>
                                        {matCols.map(col=>(
                                            <td key={col} style={{padding:'8px 12px',textAlign:'center',border:'1px solid #e2e8f0'}}>
                                                <input type="radio"
                                                    name={`matrix_${q.id}_${ri}`} value={col}
                                                    checked={(val?.[row]||'')===col}
                                                    onChange={()=>setAnswer(q.id,{...(val||{}),[row]:col})}
                                                    style={{width:18,height:18,cursor:'pointer',accentColor:'#1890ff'}}/>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            }
            case 'date':
                return <DatePicker value={val} onChange={v=>setAnswer(q.id,v)} style={{width:'100%'}}/>;
            default:
                return <Input value={val||''} onChange={e=>setAnswer(q.id,e.target.value)}/>;
        }
    };

    // ── Repeat group renderer ─────────────────────────────────────────────────
    const renderRepeatGroup = (groupLabel, groupQuestions) => {
        const secDef      = survey?.sections?.find(s => s.is_repeat && s.title === groupLabel);
        const maxCount    = secDef?.repeat_max || 3;
        const activeCount = repeatCounts[groupLabel] || 1;
        const instance1Qs = groupQuestions.filter(q => q.repeat_instance === 1);
        const fieldNames  = instance1Qs.map(q => q.xlsform_name);
        const getInstanceQs = (i) =>
            fieldNames.map(name =>
                groupQuestions.find(q => q.xlsform_name === name && q.repeat_instance === i)
            ).filter(Boolean);

        return (
            <Card key={groupLabel} size="small" style={{marginBottom:16,
                border:'2px solid #7c3aed',borderRadius:10,overflow:'hidden'}}>
                {/* Group header */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                    marginBottom:12,padding:'8px 12px',background:'#f5f3ff',
                    borderRadius:6,border:'1px solid #e9d5ff'}}>
                    <div>
                        <span style={{fontWeight:700,fontSize:14,color:'#7c3aed'}}>
                            👥 {groupLabel}
                        </span>
                        <span style={{fontSize:12,color:'#888',marginLeft:8}}>
                            ({activeCount} of {maxCount} entr{activeCount===1?'y':'ies'})
                        </span>
                    </div>
                    <div style={{display:'flex',gap:6,alignItems:'center'}}>
                        <Button size="small" icon={<MinusOutlined/>}
                            disabled={activeCount <= 1}
                            onClick={() => setRepeatCount(groupLabel, activeCount - 1, maxCount)}
                            style={{borderColor:'#7c3aed',color:'#7c3aed'}}>
                            Remove
                        </Button>
                        <Button size="small" icon={<PlusOutlined/>}
                            disabled={activeCount >= maxCount}
                            onClick={() => setRepeatCount(groupLabel, activeCount + 1, maxCount)}
                            type="primary"
                            style={{background:'#7c3aed',borderColor:'#7c3aed'}}>
                            Add
                        </Button>
                    </div>
                </div>

                {/* Active instances */}
                {Array.from({length: activeCount}, (_, idx) => {
                    const i      = idx + 1;
                    const instQs = getInstanceQs(i);
                    return (
                        <div key={i} style={{
                            marginBottom: i < activeCount ? 16 : 0,
                            padding:'12px',
                            background: i % 2 === 0 ? '#faf5ff' : '#fff',
                            borderRadius:8,
                            border:'1px solid #e9d5ff'
                        }}>
                            <div style={{fontWeight:700,fontSize:12,color:'#7c3aed',
                                marginBottom:10,textTransform:'uppercase',letterSpacing:0.5}}>
                                Entry {i}
                            </div>
                            {instQs.map(q => (
                                shouldShow(q) && (
                                    <div key={q.id} style={{marginBottom:12}}>
                                        <div style={{marginBottom:6}}>
                                            <span style={{fontSize:13,fontWeight:600,color:'#1e293b'}}>
                                                {q.label.replace(new RegExp(`^${groupLabel}\\s+${i}\\s+—\\s+`), '')}
                                            </span>
                                            {q.required && i === 1 &&
                                                <Tag color="red" style={{marginLeft:6,fontSize:10}}>Required</Tag>}
                                            {q.helper_text &&
                                                <div style={{fontSize:11,color:'#94a3b8',marginTop:2}}>{q.helper_text}</div>}
                                        </div>
                                        {renderQ(q)}
                                    </div>
                                )
                            ))}
                        </div>
                    );
                })}
            </Card>
        );
    };

    // ── Render question list for current section ───────────────────────────────
    const renderQuestionList = (questions) => {
        const rendered = new Set();
        let normalIdx  = 0;
        return questions.filter(q => shouldShow(q)).map((q) => {
            if (q.repeat_group) {
                if (rendered.has(q.repeat_group)) return null;
                rendered.add(q.repeat_group);
                const groupQs = questions.filter(qq => qq.repeat_group === q.repeat_group);
                return renderRepeatGroup(q.repeat_group, groupQs);
            }
            normalIdx++;
            return (
                <Card key={q.id} size="small" style={{marginBottom:12,
                    borderLeft:`3px solid ${answers[q.id]!==undefined?'#16a34a':'#e2e8f0'}`}}>
                    <div style={{marginBottom:10}}>
                        <span style={{fontWeight:600,fontSize:14,color:'#1e293b'}}>{normalIdx}. {substituteLabel(q.label)}</span>
                        {q.required && <Tag color="red" style={{marginLeft:8,fontSize:10}}>Required</Tag>}
                        {q.helper_text &&
                            <div style={{fontSize:12,color:'#94a3b8',marginTop:4}}>{q.helper_text}</div>}
                    </div>
                    {renderQ(q)}
                </Card>
            );
        }).filter(Boolean);
    };

    // ── Loading / error / submitted states ────────────────────────────────────
    if (loading) return (
        <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'100vh',background:'#f8fafc'}}>
            <Spin size="large"/>
        </div>
    );
    if (error) return (
        <div style={{minHeight:'100vh',background:'#f0f4f8',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div style={{maxWidth:500,padding:32,background:'#fff',borderRadius:16,
                boxShadow:'0 4px 24px rgba(0,0,0,0.08)',textAlign:'center'}}>
                <div style={{fontSize:48,marginBottom:16}}>
                    {error.includes('closed')?'🔒':error.includes('started')?'⏳':'❌'}
                </div>
                <h2 style={{color:'#0d2137',margin:'0 0 8px'}}>
                    {error.includes('closed')?'Survey Closed':error.includes('started')?'Not Yet Open':'Survey Unavailable'}
                </h2>
                <p style={{color:'#64748b',lineHeight:1.6}}>{error}</p>
                <div style={{marginTop:20,fontSize:12,color:'#94a3b8'}}>Ministry of Health · Kingdom of Eswatini</div>
            </div>
        </div>
    );
    if (submitted) return (
        <div style={{minHeight:'100vh',background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div style={{textAlign:'center',padding:40,background:'#fff',borderRadius:16,
                boxShadow:'0 4px 24px rgba(0,0,0,0.08)',maxWidth:480}}>
                <div style={{fontSize:72,marginBottom:16}}>✅</div>
                <h2 style={{color:'#16a34a',margin:0}}>Thank You!</h2>
                <p style={{color:'#888',marginTop:8,fontSize:15}}>Your response has been recorded successfully.</p>
                {survey?.program_name && <Tag color="blue" style={{marginTop:8}}>{survey.program_name}</Tag>}
            </div>
        </div>
    );
    if (!survey) return null;

    const questionsByName = {};
    (survey.questions || []).forEach(q => { if (q.xlsform_name) questionsByName[q.xlsform_name] = q; });

    const computedValues = {};
    const getFieldValue = (name) => {
        if (computedValues[name] !== undefined) return computedValues[name];
        const q = questionsByName[name];
        if (!q) return '';
        const val = answers[q.id];
        return val !== undefined && val !== null ? val : '';
    };
    (survey.questions || []).forEach(q => {
        if (q.type === 'calculated' && q.calculation) {
            try {
                computedValues[q.xlsform_name] = evaluateExpression(q.calculation, getFieldValue);
            } catch (e) {
                computedValues[q.xlsform_name] = '';
            }
        }
    });

    const substituteLabel = (label) => {
        if (!label || !label.includes('${')) return label;
        return label.replace(/\$\{(\w+)\}/g, (match, name) => {
            const v = getFieldValue(name);
            return (v !== undefined && v !== null && v !== '') ? String(v) : match;
        });
    };

    const sections         = survey.sections?.length ? survey.sections : [{ id: null, title: 'Questions' }];
    const currentSec       = sections[section];
    const currentQuestions = survey.questions?.filter(q =>
        q.type !== 'calculated' &&
        (currentSec.id ? (!q.section_id || String(q.section_id) === String(currentSec.id)) : true)
    ) || [];
    const isLast   = section >= sections.length - 1;
    const visibleQ = (survey.questions || []).filter(q => q.type !== 'calculated' && shouldShow(q));
    const totalQ   = visibleQ.length || 0;
    const answered = visibleQ.filter(q =>
        answers[q.id] !== undefined && answers[q.id] !== '' && answers[q.id] !== null).length;
    const progress = totalQ ? Math.round(answered / totalQ * 100) : 0;
    const deadline = survey.period_end
        ? new Date(survey.period_end).toLocaleDateString('en-GB', {day:'numeric',month:'long',year:'numeric'})
        : null;
    const startDate = survey.period_start
        ? new Date(survey.period_start).toLocaleDateString('en-GB', {day:'numeric',month:'long',year:'numeric'})
        : null;

    return (
        <div style={{minHeight:'100vh',background:'#f0f4f8'}}>
            {/* Branded header */}
            <div style={{background:'linear-gradient(135deg,#0d2137 0%,#1e3a5f 60%,#1a4f7a 100%)',padding:'0',boxShadow:'0 4px 20px rgba(0,0,0,0.3)'}}>
                <div style={{background:'rgba(0,0,0,0.2)',padding:'6px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{fontSize:11,color:'rgba(255,255,255,0.7)',letterSpacing:1.5,textTransform:'uppercase'}}>Kingdom of Eswatini</div>
                    <img src={flagImg} alt="Eswatini Flag" style={{height:20,borderRadius:2,boxShadow:'0 1px 4px rgba(0,0,0,0.3)'}}/>
                </div>
                <div style={{padding:'24px 40px',display:'flex',alignItems:'center',gap:24}}>
                    <img src={coaImg} alt="Coat of Arms" style={{height:90,filter:'drop-shadow(0 2px 8px rgba(0,0,0,0.4))',flexShrink:0}}/>
                    <div style={{flex:1,textAlign:'center'}}>
                        <div style={{fontSize:13,color:'rgba(255,255,255,0.7)',fontWeight:600,letterSpacing:1,marginBottom:4}}>Ministry of Health</div>
                        <h1 style={{color:'#fff',margin:'0 0 8px',fontSize:24,fontWeight:800,lineHeight:1.3}}>{survey.title}</h1>
                        {survey.program_name && (
                            <div style={{display:'inline-block',background:'rgba(255,255,255,0.15)',
                                border:'1px solid rgba(255,255,255,0.3)',borderRadius:20,
                                padding:'3px 14px',fontSize:12,color:'#fff',marginBottom:6}}>
                                📋 {survey.program_name}
                            </div>
                        )}
                        {survey.description && (
                            <p style={{color:'rgba(255,255,255,0.7)',margin:'6px 0 0',fontSize:13,lineHeight:1.5}}>
                                {survey.description}
                            </p>
                        )}
                    </div>
                    <img src={coaImg} alt="" style={{height:90,filter:'drop-shadow(0 2px 8px rgba(0,0,0,0.4))',flexShrink:0,opacity:0.7}}/>
                </div>
                <div style={{background:'rgba(0,0,0,0.15)',padding:'10px 40px',display:'flex',gap:24,flexWrap:'wrap',alignItems:'center'}}>
                    {startDate && deadline && (
                        <div style={{display:'flex',alignItems:'center',gap:6,color:'rgba(255,255,255,0.8)',fontSize:12}}>
                            <span>📅</span>
                            <span>Collection Period: <strong style={{color:'#fff'}}>{startDate} — {deadline}</strong></span>
                        </div>
                    )}
                    {deadline && !startDate && (
                        <div style={{display:'flex',alignItems:'center',gap:6,color:'rgba(255,255,255,0.8)',fontSize:12}}>
                            <span>⏰</span>
                            <span>Deadline: <strong style={{color:'#facc15'}}>{deadline}</strong></span>
                        </div>
                    )}
                    <div style={{display:'flex',alignItems:'center',gap:6,color:'rgba(255,255,255,0.8)',fontSize:12}}>
                        <span>📝</span><span>{totalQ} questions</span>
                    </div>
                    {survey.is_anonymous == 1 && (
                        <div style={{display:'flex',alignItems:'center',gap:6,color:'rgba(255,255,255,0.8)',fontSize:12}}>
                            <span>🔒</span><span>Anonymous</span>
                        </div>
                    )}
                </div>
            </div>

            <div style={{width:'100%',maxWidth:'100%',padding:'24px 32px'}}>
                {/* Progress */}
                <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
                    <Progress percent={progress} size="small" style={{flex:1}}/>
                    <span style={{fontSize:12,color:'#888',whiteSpace:'nowrap'}}>{answered}/{totalQ} answered</span>
                </div>

                {/* Facility + Respondent */}
                {section === 0 && (
                    <Card size="small" style={{marginBottom:16,borderLeft:'4px solid #1890ff'}}>
                        <div style={{fontWeight:700,marginBottom:12,color:'#0d2137'}}>Your Details</div>
                        <div style={{marginBottom:12}}>
                            <div style={{fontSize:12,color:'#888',marginBottom:4}}>Facility <span style={{color:'red'}}>*</span></div>
                            {facilities.length === 1 ? (
                                <div style={{
                                    padding:'8px 12px', borderRadius:6, background:'#f0f5ff',
                                    border:'1px solid #adc6ff', fontWeight:600, color:'#0d2137',
                                    display:'flex', alignItems:'center', gap:8,
                                }}>
                                    <span>🔒</span>
                                    <span>{facilities[0].business_name}{facilities[0].located_region ? ` — ${facilities[0].located_region}` : ''}</span>
                                </div>
                            ) : (
                                <Select showSearch value={facilityId} onChange={setFacilityId}
                                    placeholder="Select your facility" style={{width:'100%'}}
                                    filterOption={(input, option) => {
                                                                                const f = facilities.find(fac => fac.id === option.value);
                                        if (!f) return false;
                                        const text = String(f.business_name || '') + ' ' + String(f.located_region || '');
                                        return text.toLowerCase().includes(String(input || '').toLowerCase());
                                    }}>
                                    {facilities.map(f=>(
                                        <Option key={f.id} value={f.id}>{f.business_name} — {f.located_region}</Option>
                                    ))}
                                </Select>
                            )}
                        </div>
                        <div>
                            <div style={{fontSize:12,color:'#888',marginBottom:4}}>Your Name (optional)</div>
                            <Input value={respondent} onChange={e=>setRespondent(e.target.value)} placeholder="Enter your name"/>
                        </div>
                    </Card>
                )}

                {/* Section tabs */}
                {sections.length > 1 && (
                    <div style={{display:'flex',gap:4,marginBottom:16,overflowX:'auto',paddingBottom:4}}>
                        {sections.map((sec,i)=>(
                            <button key={sec.id||i} onClick={()=>setSection(i)}
                                style={{padding:'6px 14px',borderRadius:6,
                                    border:'1px solid '+(i===section?'#1890ff':'#e5e7eb'),
                                    background:i===section?'#1890ff':'#fff',
                                    color:i===section?'#fff':'#555',
                                    cursor:'pointer',whiteSpace:'nowrap',fontSize:12,fontWeight:600,flexShrink:0,
                                    ...(sec.is_repeat?{borderColor:'#7c3aed',color:i===section?'#fff':'#7c3aed',background:i===section?'#7c3aed':'#faf5ff'}:{})}}>
                                {sec.is_repeat ? '👥 ' : ''}{sec.title}
                            </button>
                        ))}
                    </div>
                )}

                {/* Questions */}
                {renderQuestionList(currentQuestions)}

                {/* Navigation */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:24,gap:8,flexWrap:'wrap'}}>
                    <div style={{display:'flex',gap:8}}>
                        {section > 0 && <Button onClick={()=>setSection(s=>s-1)}>← Back</Button>}
                        <Button icon={<SaveOutlined/>} loading={submitting} onClick={saveDraft}
                            style={{borderColor:'#d97706',color:'#d97706'}}>
                            Save Draft
                        </Button>
                    </div>
                    <div style={{display:'flex',gap:8}}>
                        {!isLast ? (
                            <Button type="primary" onClick={()=>setSection(s=>s+1)}>Next →</Button>
                        ) : (
                            <Button type="primary" size="large" icon={<SendOutlined/>}
                                loading={submitting} onClick={submit}
                                style={{background:'#16a34a',borderColor:'#16a34a',padding:'0 32px'}}>
                                Submit Survey
                            </Button>
                        )}
                    </div>
                </div>

                <div style={{textAlign:'center',marginTop:32,padding:'20px 0',borderTop:'1px solid #e2e8f0'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,marginBottom:8}}>
                        <img src={coaImg} alt="" style={{height:32,opacity:0.5}}/>
                        <span style={{fontSize:12,color:'#94a3b8'}}>Ministry of Health · Kingdom of Eswatini · National Quality Management Programme</span>
                        <img src={flagImg} alt="" style={{height:16,opacity:0.5,borderRadius:2}}/>
                    </div>
                    <div style={{fontSize:11,color:'#cbd5e1'}}>Your response is saved securely · Powered by MoH Assessment Platform</div>
                </div>
            </div>
        </div>
    );
}

